export const pluralize = (word: string, count: number) => {
  if (count === 1) {
    return word;
  }
  // Handle some basic irregular pluralization rules
  if (
    word.endsWith('s') ||
    word.endsWith('x') ||
    word.endsWith('z') ||
    word.endsWith('sh') ||
    word.endsWith('ch')
  ) {
    return `${word}es`;
  }

  // Handle words ending with 'y', which change to 'ies' in plural form
  if (word.endsWith('y') && !/[aeiou]y$/.test(word)) {
    return `${word.slice(0, -1)}ies`;
  }
  // Default rule: add 's'
  return `${word}s`;
};

export const choosePlural = (
  singular: string,
  plural: string,
  count: number
) => {
  return count === 1 ? singular : plural;
};

const formatter = new Intl.NumberFormat('en-US', {});
export const xCount = (word: string, count: number) => {
  return `${formatter.format(count)} ${pluralize(word, count)}`;
};

export const capitalize = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const capitalizeWords = (words: string) => {
  return words.split(' ').map(capitalize).join(' ');
};

export const toSnakeCase = (str: string) => {
  return str.replace(/[A-Z]+/g, (capitalLetters, position) => {
    const underscore = position > 0 ? '_' : '';

    if (capitalLetters.length > 1) {
      const nextCharIsLowercase =
        str[position + capitalLetters.length]?.match(/[a-z]/);

      if (nextCharIsLowercase) {
        const lastCapital = capitalLetters.slice(-1);
        const otherCapitals = capitalLetters.slice(0, -1);
        return `${underscore}${otherCapitals.toLowerCase()}_${lastCapital.toLowerCase()}`;
      }
    }

    return underscore + capitalLetters.toLowerCase();
  });
};

export const filterAndSnakeCaseKeys = (obj, allowedKeys: string[]) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (allowedKeys.includes(key)) {
      const snakeCaseKey = toSnakeCase(key);
      acc[snakeCaseKey] = value;
    }
    return acc;
  }, {});
};

export const getTopLevelError = (trace: string): string => {
  const firstErrorMessageLine = trace
    .split(/[\r\n]/)[0]
    .replace(/Traceback \(most recent call last\):/, '');
  // Use a regex to find the first line that matches an exception type (like KeyError) and the corresponding message
  const match = trace.match(/(\w+Error: .+)/);
  if (!match) {
    // If nothing obvious, return the raw error
    return trace.trim();
  } else if (!firstErrorMessageLine.includes('Error')) {
    return [firstErrorMessageLine, 'Hint:', match[1]].join('\n\n').trim();
  } else {
    return firstErrorMessageLine.trim();
  }
};

export const lowerCaseIncludes = (str: string, substr: string) => {
  return str.toLowerCase().includes(substr.toLowerCase());
};

/**
 * Removes quotation marks (single or double) from the beginning and end of a string.
 * If the string doesn't start and end with the same type of quote, it returns the original string.
 *
 * @param str - The string to trim quotes from
 * @returns The string with quotes removed from the beginning and end, or the original string if quotes don't match
 */
export const trimQuotes = (str: string): string => {
  if (!str || str.length < 2) return str;

  const firstChar = str[0];
  const lastChar = str[str.length - 1];

  // Check if the string starts and ends with the same type of quote
  if (
    (firstChar === '"' && lastChar === '"') ||
    (firstChar === "'" && lastChar === "'")
  ) {
    return str.slice(1, -1);
  }

  return str;
};
